import axios, { type AxiosError } from "axios";
import { api } from "@/lib/axios";
import type { ProfileUpdateRequest, User } from "@/lib/humhub-api/types";

function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

interface ImageUploadResponse {
  success: boolean;
  image: {
    url: string;
    user_id: number;
  };
}

export const usersApi = {
  getUserById: async (userId: number): Promise<User> => {
    try {
      const response = await api.get<User>(`user/${userId}`);

      if (!response.data) {
        throw new Error("Empty response from user API");
      }

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("User API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred while fetching user information");
    }
  },

  getUserByGuid: async (guid: string): Promise<User> => {
    try {
      const response = await api.get<User>(`user/guid/${guid}`);

      if (!response.data) {
        throw new Error("Empty response from user API");
      }

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }
        console.error("User API Error:", error.response.data);
        throw error;
      }
      throw new Error("Unexpected error occurred while fetching user information");
    }
  },

  updateUserProfile: async (updateData: ProfileUpdateRequest): Promise<User> => {
    try {
      const response = await api.put<User>("user/update-own-settings", updateData, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error("Update user profile API Error:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          headers: error.response?.headers,
          data: error.response?.data,
          requestHeaders: error.config?.headers,
          requestUrl: error.config?.url,
        });

        if (!error.response) {
          throw new Error("ネットワークエラーまたはCORS設定に問題があります");
        }

        throw error;
      }
      throw new Error("Unexpected error occurred while updating user profile");
    }
  },
  // プロフィール画像アップロード
  uploadProfileImage: async (file: File): Promise<ImageUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append("profileImage", file);

      const response = await api.post<ImageUploadResponse>("user/upload-profile-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.data.success) {
        throw new Error("Upload failed");
      }

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error("Upload profile image API Error:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });

        if (!error.response) {
          throw new Error("Please check your connection");
        }

        throw error;
      }
      throw new Error("Unexpected error occurred while uploading profile image");
    }
  },

  // バナー画像アップロード
  uploadBannerImage: async (file: File): Promise<ImageUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append("bannerImage", file);

      const response = await api.post<ImageUploadResponse>("user/upload-banner-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.data.success) {
        throw new Error("Upload failed");
      }

      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        console.error("Upload banner image API Error:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });

        if (!error.response) {
          throw new Error("Please check your connection");
        }

        throw error;
      }
      throw new Error("Unexpected error occurred while uploading banner image");
    }
  },

  blockUser: async (userId: number): Promise<boolean> => {
    try {
      const response = await api.post<{ success: boolean }>(`user/block/${userId}`);

      if (!response.data) {
        throw new Error("Empty or invalid response from user API");
      }

      return response.data.success;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }

        console.error("Block user API Error:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });

        throw error;
      }
      throw new Error("Unexpected error occurred while blocking the user");
    }
  },

  unblockUser: async (userId: number): Promise<boolean> => {
    try {
      const response = await api.post<{ success: boolean }>(`user/unblock/${userId}`);

      if (!response.data) {
        throw new Error("Empty or invalid response from user API");
      }

      return response.data.success;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }

        console.error("Unblock user API Error:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });

        throw error;
      }
      throw new Error("Unexpected error occurred while unblocking the user");
    }
  },

  blockedUsersList: async (): Promise<string[]> => {
    try {
      const response = await api.get<{ blocked_user_ids: string[] }>("user/blocked-users");
      if (!response.data) {
        throw new Error("Empty response from blocked users API");
      }
      return response.data.blocked_user_ids || [];
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (!error.response) {
          throw new Error("Please check your connection");
        }

        console.error("Blocked users list API Error:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        });

        throw error;
      }
      throw new Error("Unexpected error occurred while fetching blocked users");
    }
  },
};
